import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { VIBES, type Vibe } from "@majourneys/shared";
import { useTripActions } from "../../state/trip-context";
import { useDiscoverCities } from "../../state/discover-context";
import { useFocusTrap } from "../../hooks/use-focus-trap";
import { DEFAULT_TRIP_PEOPLE } from "../../state/trip-storage";
import {
  MAX_TRIP_DAYS,
  TripDatesField,
  TripPartyFields,
  daysInRange,
} from "./trip-when-fields";
import { partySize, type TripParty } from "../../lib/trip-party";
import { fitRoute, type RouteLeg } from "../../lib/trip-route";
import { TripRouteFields } from "./trip-route-fields";

import { Icon } from "@majourneys/icons";
interface CreateTripDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

/**
 * Where a new trip goes when the guest does not choose. Marrakech, because it
 * is where most trips are planned and most of the catalogue is, rather than
 * whichever city sorts first (that was Agadir).
 */
export const DEFAULT_TRIP_CITY = "marrakech";

export function CreateTripDialog({ open, onClose }: CreateTripDialogProps) {
  const { t } = useTranslation();
  const { createTrip } = useTripActions();
  const cities = useDiscoverCities();
  const [name, setName] = useState(t("trip.default_name"));
  // The route: one leg is the old "a city and how many days"; more legs are
  // a trip across several cities. An empty city is filled in below once the
  // cities load.
  const [legs, setLegs] = useState<readonly RouteLeg[]>([{ city: "", days: 3 }]);
  // Several, not one: a week can be food and culture and still be one trip,
  // and a single answer is what made the old style radio useless for
  // suggesting anything. `style` is derived from these on the way in.
  //
  // Starts empty, unlike the app's sheet, which pre-ticks the answer given at
  // sign-up. Nothing in this package can see the account — Discover is fed by
  // a data source, not a session — so there is nothing to seed from until one
  // is passed in. Better blank than guessed.
  const [vibes, setVibes] = useState<readonly Vibe[]>([]);
  // Optional: a guest who does not know their dates yet still gets a trip,
  // sized by the day stepper. Picking dates replaces the stepper.
  const [dates, setDates] = useState<{ start: string | null; end: string | null }>({
    start: null,
    end: null,
  });
  const [party, setParty] = useState<TripParty>({ adults: DEFAULT_TRIP_PEOPLE, childAges: [] });
  const nameInputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Trap focus while open + restore to the trigger on close (initial focus
  // goes to the name input).
  useFocusTrap(dialogRef, open, nameInputRef);

  // Default the city to Marrakech once the cities list loads (the first city
  // in the list only when Marrakech is not in it), unless the guest already
  // picked one.
  const fallbackCity =
    cities.find((c) => c.slug === DEFAULT_TRIP_CITY)?.slug ?? cities[0]?.slug ?? DEFAULT_TRIP_CITY;
  const firstCity = legs[0]?.city ?? "";
  useEffect(() => {
    if (open && firstCity === "" && cities.length > 0) {
      setLegs((current) => current.map((leg, i) => (i === 0 ? { ...leg, city: fallbackCity } : leg)));
    }
  }, [open, cities, firstCity, fallbackCity]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  // Dates fix how long the trip is; the route is fitted to them.
  const fixedTotal = dates.start
    ? Math.min(MAX_TRIP_DAYS, daysInRange(dates.start, dates.end))
    : null;
  const route = fitRoute(
    legs.map((leg) => ({ ...leg, city: leg.city || fallbackCity })),
    fixedTotal,
  );
  const setDayCount = (update: (days: number) => number) =>
    setLegs((current) => current.map((leg, i) => (i === 0 ? { ...leg, days: update(leg.days) } : leg)));
  const dayCount = route[0]?.days ?? 1;

  const toggleVibe = (vibe: Vibe) => {
    setVibes((current) =>
      current.includes(vibe)
        ? current.filter((v) => v !== vibe)
        : [...current, vibe],
    );
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (name.trim().length === 0) return;
    const days = route.reduce((sum, leg) => sum + leg.days, 0);
    createTrip({
      name: name.trim(),
      dayCount: days,
      city: route[0]?.city ?? fallbackCity,
      // Only a real route is sent: a one-city trip stays shaped as it always
      // was, with no city on its days.
      ...(route.length > 1 ? { route } : {}),
      vibes,
      startDate: dates.start ?? undefined,
      people: partySize(party),
      childAges: party.childAges,
    });
    onClose();
    setName(t("trip.default_name"));
    setLegs([{ city: "", days: 3 }]);
    setVibes([]);
    setDates({ start: null, end: null });
    setParty({ adults: DEFAULT_TRIP_PEOPLE, childAges: [] });
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="mj-trip-dialog-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="mj-trip-dialog mj-trip-dialog--with-dropdown mj-trip-dialog--create"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mj-trip-dialog-title"
      >
        <header className="mj-trip-dialog__header">
          <div>
            <span>{t("trip.new_trip")}</span>
            <strong id="mj-trip-dialog-title">{t("trip.plan_trip")}</strong>
          </div>
          <button
            type="button"
            className="mj-trip-dialog__close"
            onClick={onClose}
            aria-label={t("common.close")}
          >
            <Icon name="ri-close-line" aria-hidden="true" />
          </button>
        </header>

        <form className="mj-trip-dialog__form" onSubmit={handleSubmit}>
          <label className="mj-trip-dialog__field">
            <span>{t("trip.trip_name")}</span>
            <input
              ref={nameInputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={60}
            />
          </label>

          {cities.length > 0 && (
            <TripRouteFields
              cities={cities}
              legs={route}
              fixedTotal={fixedTotal}
              onChange={setLegs}
            />
          )}

          <TripDatesField start={dates.start} end={dates.end} onChange={setDates} />

          {/* Without dates the trip is still sized, by hand. With them the
              range already says how long it is, and a route sizes each city
              on its own row. */}
          {!dates.start && route.length === 1 && (
            <label className="mj-trip-dialog__field">
              <span>{t("trip.how_many_days")}</span>
              <div className="mj-trip-dialog__days">
                <button
                  type="button"
                  onClick={() => setDayCount((d) => Math.max(1, d - 1))}
                  aria-label={t("trip.fewer_days")}
                >
                  <Icon name="ri-subtract-line" aria-hidden="true" />
                </button>
                <strong>{dayCount}</strong>
                <button
                  type="button"
                  onClick={() => setDayCount((d) => Math.min(14, d + 1))}
                  aria-label={t("trip.more_days")}
                >
                  <Icon name="ri-add-line" aria-hidden="true" />
                </button>
              </div>
            </label>
          )}

          <TripPartyFields value={party} onChange={setParty} />

          {/* Buttons, not radios: the answer is a set, and `aria-pressed`
              says which of them are on. The hidden radio input the style
              picker used could not say that, and could not be tabbed to. */}
          <fieldset className="mj-trip-dialog__field">
            <span id="mj-trip-dialog-vibes">
              {t("trip.vibe_label")}
            </span>
            <div
              className="mj-trip-dialog__styles"
              role="group"
              aria-labelledby="mj-trip-dialog-vibes"
            >
              {VIBES.map((vibe) => {
                const on = vibes.includes(vibe);
                return (
                  <button
                    key={vibe}
                    type="button"
                    aria-pressed={on}
                    className={`mj-trip-dialog__style${on ? " is-active" : ""}`}
                    onClick={() => toggleVibe(vibe)}
                  >
                    {t(`trip.vibe.${vibe}`)}
                  </button>
                );
              })}
            </div>
            <p className="mj-trip-dialog__hint">
              {vibes.length === 0
                ? t("trip.vibe_hint_none")
                : t("trip.vibe_hint")}
            </p>
          </fieldset>

          <footer className="mj-trip-dialog__footer">
            <button
              type="button"
              className="mj-trip-dialog__cancel"
              onClick={onClose}
            >
              {t("trip.cancel")}
            </button>
            <button type="submit" className="mj-trip-dialog__submit">
              <Icon name="ri-check-line" aria-hidden="true" />
              {t("trip.create_trip")}
            </button>
          </footer>
        </form>
      </div>
    </div>,
    document.body,
  );
}
